import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ConfirmDialog,
  fromServerInput,
  symbolsOf,
  useApi,
  useDisplayZone,
  useMoneyFormat,
  usePrefsValue,
  useServerOffset,
} from '@adiox/ui';
import { effectiveWatchList } from '../market/marketWatchModel';
import { useSymbols } from '../market/useSymbols';
import { useWorkspace } from '../workspace/workspaceStore';
import type { OrderRequest } from './tradeClient';
import {
  KIND_LABELS,
  TICKET_KINDS,
  normalizeVolume,
  pendingPriceProblem,
  sideAllowed,
  sideOfOrderType,
  stopLimitProblem,
  stopsProblem,
  volumeProblem,
  type OrderType,
  type Side,
  type TicketKind,
} from './tradeRules';
import { LEVEL_UNITS, UNIT_LABELS, levelPrice, type LevelKind, type LevelUnit } from './levels';
import { lossRoom, riskWarning } from '../challenge/model';
import { useChallenge } from '../challenge/useChallenge';
import { SymbolPicker } from './SymbolPicker';
import { lotsForRisk, quickVolumes, resultAt } from './tradeMath';
import { useMoneyPerLot } from './useMoneyPerLot';
import { useTradeClient, useTradeGate } from './useTrading';

const DEFAULT_SYMBOL = 'EURUSD';
const num = (text: string) => (text.trim() === '' ? Number.NaN : Number(text));

interface Form {
  symbol: string;
  kind: TicketKind;
  volume: string;
  price: string;
  stopLimit: string;
  stopLoss: string;
  takeProfit: string;
  /** How stopLoss and takeProfit are typed: a price, or a distance in points, pips, money or % of the balance. */
  unit: LevelUnit;
  expiration: 'Gtc' | 'Today' | 'Specified';
  expirationTime: string;
  comment: string;
  /** The risk for the lot-size calculator, as % of the balance or an amount of money. */
  risk: string;
  riskUnit: 'percent' | 'money';
}

const blank = (symbol: string, volumeMin: number): Form => ({
  symbol,
  kind: 'Market',
  volume: String(volumeMin),
  price: '',
  stopLimit: '',
  stopLoss: '',
  takeProfit: '',
  unit: 'price',
  expiration: 'Gtc',
  expirationTime: '',
  comment: '',
  risk: '1',
  riskUnit: 'percent',
});

type Notice = { tone: 'ok' | 'error'; text: string } | null;

const field =
  'w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent disabled:opacity-50';
const label = 'mb-1 block text-xs uppercase tracking-wide text-muted';

/**
 * `closeContext`: when the ticket is opened for an existing position (the classic terminal's Order window), the button on
 * the opposite side of the position closes it at the market (fully, or partly if the volume typed is less) instead of
 * opening a new trade. The button on the same side still opens an additional trade, as MT5 itself allows.
 */
interface CloseContext {
  ticket: number;
  side: Side;
  volume: number;
  symbol: string;
  priceOpen: number;
}

/** The order ticket: market and pending orders with volume rules, SL/TP, a margin preview and clear refusals. */
export function OrderTicket({
  closeContext,
  onSuccess,
}: { closeContext?: CloseContext; onSuccess?: () => void } = {}) {
  const api = useApi();
  const active = useWorkspace((s) => s.activeSymbol);
  const symbols = useSymbols();
  const watched = usePrefsValue((s) => s.data.marketWatch);
  // Same symbols as Market Watch's own Symbols list, in the same order — as in MT5, add it there first to trade it.
  const watchList = effectiveWatchList(symbolsOf({ marketWatch: watched }), symbols.data ?? []);
  const options = watchList
    .map((name) => symbols.data?.find((s) => s.name === name))
    .filter((s): s is NonNullable<typeof s> => s !== undefined);
  const client = useTradeClient();
  // The expiry is typed in the time zone the user chose (server time unless they changed it).
  const zone = useDisplayZone();
  const serverOffset = useServerOffset();
  const fmt = useMoneyFormat();
  const [stored, setStored] = useState<Form | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  /** A send waiting for the user's yes: because they asked to confirm market orders, or because it risks a loss limit. */
  const [confirming, setConfirming] = useState<{ side: Side; warning: string | null } | null>(null);
  const challenge = useChallenge().data;
  const [sizing, setSizing] = useState(false);
  const confirmMarket = usePrefsValue((s) => s.data.confirmMarketOrders === true);
  const inFlight = useRef(false); // state alone lets two clicks in one instant both through
  const attempt = useRef<{ key: string; body: string } | null>(null);

  const symbol = stored?.symbol && !active ? stored.symbol : (active ?? stored?.symbol ?? DEFAULT_SYMBOL);
  const { reason, quote, info, account, login } = useTradeGate(symbol);
  const form: Form =
    stored && stored.symbol === symbol
      ? stored
      : blank(symbol, closeContext?.volume ?? info?.volumeMin ?? 0.01);
  const set = (patch: Partial<Form>) => setStored({ ...form, ...patch });
  const digits = info?.digits ?? 5;
  const priceStep = String(1 / 10 ** digits);

  const isMarket = form.kind === 'Market';
  const volume = num(form.volume);
  const previewSides: Side[] = isMarket ? ['Buy', 'Sell'] : [sideOfOrderType(form.kind as OrderType)];
  const volumeIssue = info ? volumeProblem(volume, info) : null;

  const preview = useQuery({
    queryKey: ['margin-preview', login, symbol, previewSides.join(), volume],
    enabled: login !== undefined && info !== undefined && volumeIssue === null,
    staleTime: 5_000,
    placeholderData: (previous) => previous,
    queryFn: async () =>
      Promise.all(
        previewSides.map(async (side) => {
          const { data } = await api.GET('/api/v1/accounts/{login}/margin-preview', {
            params: { path: { login: login! }, query: { symbol, side, volume } },
          });
          return [side, data ?? null] as const;
        }),
      ),
  });

  // Money, % of balance, the risk shown at the levels and the lot-size calculator all need the value of a price move:
  // the server's profit preview gives it per lot.
  const moneyPerLot = useMoneyPerLot(symbol, digits);
  const moneyPerPrice = moneyPerLot !== null && Number.isFinite(volume) ? moneyPerLot * volume : null;

  const entryFor = (side: Side): number | null => {
    if (!quote) return null;
    if (isMarket) return side === 'Buy' ? quote.ask : quote.bid;
    const trigger = form.kind.endsWith('StopLimit') ? num(form.stopLimit) : num(form.price);
    return Number.isFinite(trigger) ? trigger : null;
  };

  /** The SL or TP the user typed, as a price for this side; 0 when empty, null when it cannot be understood yet. */
  const levelFor = (kind: LevelKind, side: Side): number | null => {
    const text = kind === 'stopLoss' ? form.stopLoss : form.takeProfit;
    if (text.trim() === '') return 0;
    const entry = entryFor(side);
    if (entry === null) return null;
    return levelPrice(form.unit, text, kind, {
      side,
      entry,
      digits,
      moneyPerPrice: moneyPerPrice ?? null,
      balance: account?.figures.balance ?? 0,
    });
  };

  const problems = (side: Side): string | null => {
    if (!info || !quote) return null;
    const v = volumeProblem(volume, info);
    if (v) return v;
    if (!sideAllowed(info.tradeMode, side))
      return `${info.tradeMode} does not allow ${side.toLowerCase()} orders on ${symbol}.`;
    const price = num(form.price);
    const p = pendingPriceProblem(form.kind, price, quote);
    if (p) return p;
    const sl = levelFor('stopLoss', side);
    const tp = levelFor('takeProfit', side);
    if (sl === null) return `Enter the stop loss as a number in ${UNIT_LABELS[form.unit].toLowerCase()}.`;
    if (tp === null) return `Enter the take profit as a number in ${UNIT_LABELS[form.unit].toLowerCase()}.`;
    const l = stopLimitProblem(form.kind, price, num(form.stopLimit));
    if (l) return l;
    const entry = isMarket
      ? side === 'Buy'
        ? quote.ask
        : quote.bid
      : form.kind.endsWith('StopLimit')
        ? num(form.stopLimit)
        : price;
    if (form.expiration === 'Specified') {
      if (zone.mode === 'server' && serverOffset === null)
        return 'Waiting for the server time. Try again in a moment.';
      if (!fromServerInput(form.expirationTime, zone.offsetAt)) return 'Choose the expiration time.';
    }
    return stopsProblem(side, entry, sl, tp);
  };

  /** A click on Buy or Sell: asks first when the user wants market orders confirmed. */
  /**
   * A click on Buy, Sell or Place: asks first when the stop-loss risk is bigger than what a prop-firm account can still lose,
   * or when the user wants market orders confirmed.
   */
  const request = (side: Side) => {
    const warning = warningFor(side);
    if (warning || (isMarket && confirmMarket)) {
      const problem = problems(side);
      if (problem) return setNotice({ tone: 'error', text: problem });
      return setConfirming({ side, warning });
    }
    void submit(side);
  };

  /** The separate Close button, when this ticket was opened for an existing position (MT5's own Sell/Buy/Close pattern). */
  const closingVolumeIssue = info ? volumeProblem(volume, info) : null;
  const submitClose = async () => {
    if (!client || inFlight.current || !closeContext) return;
    const v = normalizeVolume(volume, info!);
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    const outcome = await client.closePosition(closeContext.ticket, v >= closeContext.volume ? undefined : v);
    inFlight.current = false;
    setBusy(false);
    if (outcome.ok) {
      setNotice({
        tone: 'ok',
        text: `Position #${closeContext.ticket} closed at ${outcome.value.price.toFixed(digits)}.`,
      });
      onSuccess?.();
    } else {
      setNotice({ tone: 'error', text: outcome.message });
    }
  };

  const submit = async (side: Side) => {
    if (!client || inFlight.current || reason) return;
    const problem = problems(side);
    if (problem) return setNotice({ tone: 'error', text: problem });
    const type = isMarket ? side : (form.kind as OrderType);
    const body: OrderRequest = {
      symbol,
      type,
      volume: normalizeVolume(volume, info!),
      ...(isMarket ? {} : { price: num(form.price) }),
      ...(form.kind.endsWith('StopLimit') ? { stopLimitPrice: num(form.stopLimit) } : {}),
      stopLoss: levelFor('stopLoss', side) ?? 0,
      takeProfit: levelFor('takeProfit', side) ?? 0,
      ...(isMarket
        ? {}
        : {
            expiration: form.expiration,
            ...(form.expiration === 'Specified'
              ? { expirationTime: fromServerInput(form.expirationTime, zone.offsetAt)! }
              : {}),
          }),
      comment: form.comment || undefined,
    };
    // A retry of an attempt whose result is unknown reuses its key, so the server trades at most once.
    const json = JSON.stringify(body);
    if (attempt.current?.body !== json) attempt.current = { key: crypto.randomUUID(), body: json };
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    const outcome = await client.placeOrder(body, attempt.current.key);
    inFlight.current = false;
    setBusy(false);
    if (outcome.ok) {
      attempt.current = null;
      const r = outcome.value;
      setNotice({
        tone: 'ok',
        text: isMarket
          ? `${side} ${body.volume} ${symbol} at ${r.price.toFixed(digits)}: position #${r.position}.`
          : `${KIND_LABELS[form.kind]} #${r.order} placed at ${body.price?.toFixed(digits)}.`,
      });
      onSuccess?.();
    } else {
      if (outcome.retcodeName) attempt.current = null; // a definite refusal: the next try is a new order
      setNotice({ tone: 'error', text: outcome.message });
    }
  };

  const previewFor = (side: Side) => preview.data?.find(([s]) => s === side)?.[1] ?? null;
  const currency = account?.currency ?? '';
  const money = (v: number) => `${fmt(v)} ${currency}`;
  const balance = account?.figures.balance ?? 0;
  /** What the order would make or lose if closed at `level` (null when it cannot be worked out yet). */
  const resultFor = (side: Side, level: number | null): number | null => {
    const entry = entryFor(side);
    if (!level || entry === null || moneyPerLot === null || !Number.isFinite(volume)) return null;
    return resultAt(side, entry, level, volume, moneyPerLot);
  };
  // A prop-firm account: what the trade would lose at its stop loss (commission both ways included), against the room left.
  const room = challenge ? lossRoom(challenge.current.progress) : null;
  const riskFor = (side: Side): number | null => {
    const result = resultFor(side, levelFor('stopLoss', side));
    if (result === null) return null;
    return Math.max(0, -result) + 2 * Math.abs(previewFor(side)?.commission ?? 0);
  };
  const warningFor = (side: Side): string | null =>
    room && form.stopLoss.trim() !== '' ? riskWarning(riskFor(side) ?? 0, room, currency) : null;
  const sideWarning = previewSides.map(warningFor).find((w) => w !== null) ?? null;

  const levelSummary = (side: Side, kind: LevelKind): string => {
    const level = levelFor(kind, side);
    if (level === null) return '…';
    if (level === 0) return '—';
    const result = resultFor(side, level);
    if (result === null) return level.toFixed(digits);
    const share = balance > 0 ? ` · ${((Math.abs(result) / balance) * 100).toFixed(2)}%` : '';
    return `${level.toFixed(digits)} (≈ ${result > 0 ? '+' : ''}${money(result)}${share})`;
  };

  /** Sets the volume so the loss at the stop loss is the chosen share of the balance or amount of money. */
  const sizeFromRisk = () => {
    if (!info || !quote) return;
    // A stop typed as a price tells the side (below the market it protects a buy); distances are the same for both.
    const side: Side = isMarket
      ? form.unit === 'price' && num(form.stopLoss) > quote.bid
        ? 'Sell'
        : 'Buy'
      : sideOfOrderType(form.kind as OrderType);
    const entry = entryFor(side);
    const raw = num(form.risk);
    const riskMoney = form.riskUnit === 'percent' ? (raw / 100) * balance : raw;
    if (entry === null) return setNotice({ tone: 'error', text: 'Enter the order price first.' });
    if (moneyPerLot === null) {
      return setNotice({ tone: 'error', text: 'Still working out money values. Try again in a moment.' });
    }
    // With money or % units the stop depends on the volume being worked out, so it is read at one lot.
    const stop =
      form.unit === 'money' || form.unit === 'percent'
        ? levelPrice(form.unit, form.stopLoss, 'stopLoss', {
            side,
            entry,
            digits,
            moneyPerPrice: moneyPerLot,
            balance,
          })
        : levelFor('stopLoss', side);
    const size = lotsForRisk(riskMoney, entry, stop ?? 0, moneyPerLot, info);
    if (!size.ok) return setNotice({ tone: 'error', text: size.message });
    set({ volume: String(size.volume) });
    setNotice({
      tone: 'ok',
      text: `${size.volume} lots: about ${money(size.risk)} at the stop loss (${side.toLowerCase()}).`,
    });
  };
  const stepVolume = (direction: 1 | -1) => {
    if (!info) return;
    set({
      volume: String(
        normalizeVolume(
          (Number.isFinite(volume) ? volume : info.volumeMin) + direction * info.volumeStep,
          info,
        ),
      ),
    });
  };

  return (
    <section className="h-full overflow-auto bg-panel p-3" aria-label="Order ticket">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isMarket) request(sideOfOrderType(form.kind as OrderType));
        }}
        className="flex flex-col gap-3"
        noValidate
      >
        <div>
          <label className={label} htmlFor="ticket-symbol">
            Symbol
          </label>
          <SymbolPicker
            id="ticket-symbol"
            className={field}
            value={symbol}
            options={options.length > 0 ? options : [{ name: symbol }]}
            onSelect={(name) => useWorkspace.getState().select(name)}
          />
        </div>
        <div>
          <label className={label} htmlFor="ticket-type">
            Type
          </label>
          <select
            id="ticket-type"
            className={field}
            value={form.kind}
            onChange={(e) => set({ kind: e.target.value as TicketKind })}
          >
            {TICKET_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={label} htmlFor="ticket-volume">
            Volume (lots)
          </label>
          <div className="flex gap-1">
            <button
              type="button"
              className="rounded-md border border-line px-3 hover:border-accent"
              aria-label="Decrease volume"
              onClick={() => stepVolume(-1)}
            >
              −
            </button>
            <input
              id="ticket-volume"
              className={`${field} num text-center`}
              inputMode="decimal"
              value={form.volume}
              onChange={(e) => set({ volume: e.target.value })}
              aria-invalid={volumeIssue !== null}
              aria-describedby="ticket-volume-hint"
            />
            <button
              type="button"
              className="rounded-md border border-line px-3 hover:border-accent"
              aria-label="Increase volume"
              onClick={() => stepVolume(1)}
            >
              +
            </button>
          </div>
          {info && (
            <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label="Quick volumes">
              {quickVolumes(info).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={volume === v}
                  onClick={() => set({ volume: String(v) })}
                  className={`num rounded border px-2 py-0.5 text-xs ${
                    volume === v ? 'border-accent text-fg' : 'border-line text-muted hover:border-accent'
                  }`}
                >
                  {v}
                </button>
              ))}
              <button
                type="button"
                aria-expanded={sizing}
                aria-controls="ticket-risk"
                onClick={() => setSizing(!sizing)}
                className="ml-auto rounded border border-line px-2 py-0.5 text-xs text-muted hover:border-accent"
              >
                Size from risk
              </button>
            </div>
          )}
          <p id="ticket-volume-hint" className={`mt-1 text-xs ${volumeIssue ? 'text-warn' : 'text-muted'}`}>
            {volumeIssue ??
              (info ? `Min ${info.volumeMin} · max ${info.volumeMax} · step ${info.volumeStep}` : '')}
          </p>
          {sizing && (
            <div
              id="ticket-risk"
              role="group"
              aria-label="Lot size from risk"
              className="mt-1 flex flex-wrap items-end gap-2 rounded-md border border-line p-2"
            >
              <div className="w-20">
                <label className={label} htmlFor="ticket-risk-value">
                  Risk
                </label>
                <input
                  id="ticket-risk-value"
                  className={`${field} num`}
                  inputMode="decimal"
                  value={form.risk}
                  onChange={(e) => set({ risk: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <label className="sr-only" htmlFor="ticket-risk-unit">
                  Risk as
                </label>
                <select
                  id="ticket-risk-unit"
                  className={field}
                  value={form.riskUnit}
                  onChange={(e) => set({ riskUnit: e.target.value as Form['riskUnit'] })}
                >
                  <option value="percent">% of balance</option>
                  <option value="money">{currency || 'Money'}</option>
                </select>
              </div>
              <button
                type="button"
                onClick={sizeFromRisk}
                className="rounded-md border border-accent px-3 py-1 text-sm hover:bg-accent/10"
              >
                Set volume
              </button>
              <p className="w-full text-xs text-muted">
                Uses the stop loss below: the volume is the most that loses no more than this at the stop.
              </p>
            </div>
          )}
        </div>

        {!isMarket && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={label} htmlFor="ticket-price">
                {form.kind.endsWith('StopLimit') ? 'Stop price' : 'Price'}
              </label>
              <input
                id="ticket-price"
                type="number"
                step={priceStep}
                className={`${field} num`}
                value={form.price}
                onChange={(e) => set({ price: e.target.value })}
              />
            </div>
            {form.kind.endsWith('StopLimit') && (
              <div>
                <label className={label} htmlFor="ticket-limit">
                  Limit price
                </label>
                <input
                  id="ticket-limit"
                  type="number"
                  step={priceStep}
                  className={`${field} num`}
                  value={form.stopLimit}
                  onChange={(e) => set({ stopLimit: e.target.value })}
                />
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={label} htmlFor="ticket-sl">
              Stop loss
            </label>
            <input
              id="ticket-sl"
              type="number"
              step={form.unit === 'price' ? priceStep : 'any'}
              className={`${field} num`}
              placeholder="none"
              value={form.stopLoss}
              onChange={(e) => set({ stopLoss: e.target.value })}
            />
          </div>
          <div>
            <label className={label} htmlFor="ticket-tp">
              Take profit
            </label>
            <input
              id="ticket-tp"
              type="number"
              step={form.unit === 'price' ? priceStep : 'any'}
              className={`${field} num`}
              placeholder="none"
              value={form.takeProfit}
              onChange={(e) => set({ takeProfit: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className={label} htmlFor="ticket-unit">
            SL / TP entered as
          </label>
          <select
            id="ticket-unit"
            className={field}
            value={form.unit}
            onChange={(e) => set({ unit: e.target.value as LevelUnit })}
          >
            {LEVEL_UNITS.map((u) => (
              <option key={u} value={u}>
                {UNIT_LABELS[u]}
              </option>
            ))}
          </select>
          {(form.stopLoss.trim() !== '' || form.takeProfit.trim() !== '') && (
            <ul
              className="num mt-1 text-xs text-muted"
              aria-label="Levels and what they risk"
              data-testid="resolved-levels"
            >
              {(isMarket ? (['Buy', 'Sell'] as Side[]) : [sideOfOrderType(form.kind as OrderType)]).map(
                (side) => (
                  <li key={side}>
                    {side}: SL {levelSummary(side, 'stopLoss')} · TP {levelSummary(side, 'takeProfit')}
                  </li>
                ),
              )}
            </ul>
          )}
          {sideWarning && (
            <p
              role="alert"
              className="mt-1 rounded-md border border-down/50 bg-down/10 px-2 py-1 text-xs text-down"
              data-testid="risk-warning"
            >
              {sideWarning}
            </p>
          )}
          {room && form.stopLoss.trim() === '' && (
            <p className="mt-1 text-xs text-warn" data-testid="no-stop-warning">
              No stop loss: nothing limits what this trade can lose ({fmt(room.daily)} {currency} left today).
            </p>
          )}
        </div>

        {!isMarket && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={label} htmlFor="ticket-expiry">
                Expiration
              </label>
              <select
                id="ticket-expiry"
                className={field}
                value={form.expiration}
                onChange={(e) => set({ expiration: e.target.value as Form['expiration'] })}
              >
                <option value="Gtc">Good till cancelled</option>
                <option value="Today">Today</option>
                <option value="Specified">Specified</option>
              </select>
            </div>
            {form.expiration === 'Specified' && (
              <div>
                <label className={label} htmlFor="ticket-expiry-time">
                  Expires at ({zone.label})
                </label>
                <input
                  id="ticket-expiry-time"
                  type="datetime-local"
                  className={field}
                  value={form.expirationTime}
                  onChange={(e) => set({ expirationTime: e.target.value })}
                />
              </div>
            )}
          </div>
        )}

        <div>
          <label className={label} htmlFor="ticket-comment">
            Comment
          </label>
          <input
            id="ticket-comment"
            className={field}
            maxLength={31}
            value={form.comment}
            onChange={(e) => set({ comment: e.target.value })}
          />
        </div>

        <dl
          className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs"
          aria-label="Margin preview"
          data-testid="margin-preview"
        >
          {previewSides.map((side) => {
            const p = previewFor(side);
            return (
              <div key={side} className="col-span-2 grid grid-cols-subgrid">
                <dt className="text-muted">Margin{isMarket ? ` (${side.toLowerCase()})` : ''}</dt>
                <dd className="num text-right">
                  {p ? `${money(p.requiredMargin)} · free after ${money(p.freeMarginAfter)}` : '—'}
                </dd>
              </div>
            );
          })}
          <dt className="text-muted">Commission</dt>
          <dd className="num text-right">
            {previewFor(previewSides[0]!) ? money(previewFor(previewSides[0]!)!.commission) : '—'}
          </dd>
        </dl>

        {reason && (
          <p
            className="rounded-md border border-warn/50 bg-warn/10 px-3 py-2 text-sm text-warn"
            data-testid="trade-blocked"
          >
            {reason}
          </p>
        )}

        {isMarket ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={busy || reason !== null || !sideAllowed(info?.tradeMode, 'Sell')}
                onClick={() => request('Sell')}
                className="mt5-sell rounded-md px-3 py-2 font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Sell
                <span className="num block text-xs font-normal">
                  {quote ? quote.bid.toFixed(digits) : '—'}
                </span>
              </button>
              <button
                type="button"
                disabled={busy || reason !== null || !sideAllowed(info?.tradeMode, 'Buy')}
                onClick={() => request('Buy')}
                className="mt5-buy rounded-md px-3 py-2 font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Buy
                <span className="num block text-xs font-normal">
                  {quote ? quote.ask.toFixed(digits) : '—'}
                </span>
              </button>
            </div>
            {closeContext && (
              <button
                type="button"
                disabled={busy || reason !== null || closingVolumeIssue !== null}
                onClick={() => void submitClose()}
                className="rounded-md bg-accent px-3 py-2 font-semibold text-accent-fg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Close #{closeContext.ticket} {closeContext.side.toLowerCase()} {closeContext.volume}{' '}
                {closeContext.symbol} {closeContext.priceOpen.toFixed(digits)} by Market
              </button>
            )}
          </>
        ) : (
          <button
            type="submit"
            disabled={busy || reason !== null}
            className="rounded-md bg-accent px-3 py-2 font-semibold text-accent-fg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Place {KIND_LABELS[form.kind]}
          </button>
        )}

        <div aria-live="polite" className="min-h-[1.5rem] text-sm">
          {notice && (
            <p
              role={notice.tone === 'error' ? 'alert' : 'status'}
              className={notice.tone === 'error' ? 'text-down' : 'text-up'}
              data-testid="ticket-notice"
            >
              {notice.text}
            </p>
          )}
        </div>
      </form>
      <ConfirmDialog
        open={confirming !== null}
        title={
          !confirming
            ? ''
            : confirming.warning
              ? 'This trade can break your loss limit'
              : `${confirming.side} ${form.volume} ${symbol} at market?`
        }
        confirmLabel={!confirming ? '' : confirming.warning ? 'Send anyway' : `${confirming.side} now`}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          const side = confirming?.side;
          setConfirming(null);
          if (side) void submit(side);
        }}
      >
        {confirming && (
          <>
            {confirming.warning && (
              <p className="mb-2 text-down" data-testid="risk-confirm">
                {confirming.warning}
              </p>
            )}
            <ul className="num flex flex-col gap-1">
              <li>
                {isMarket ? `${confirming.side} ${form.volume} ${symbol} at market` : KIND_LABELS[form.kind]}
                {' · '}
                price now: {quote ? (confirming.side === 'Buy' ? quote.ask : quote.bid).toFixed(digits) : '—'}
              </li>
              <li>Stop loss: {levelSummary(confirming.side, 'stopLoss')}</li>
              <li>Take profit: {levelSummary(confirming.side, 'takeProfit')}</li>
              {!confirming.warning && (
                <li className="text-muted">You can turn this question off in Settings.</li>
              )}
            </ul>
          </>
        )}
      </ConfirmDialog>
    </section>
  );
}
